import { useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  useForgetConnections,
  useTopologyResync,
  type UnconfirmedConnection,
} from '@/hooks/useTopologyResync';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string | null | undefined;
  regionId?: string;
}

function linkLabel(link: UnconfirmedConnection): string {
  const port = link.sourceInterfaceName ? ` ${link.sourceInterfaceName}` : '';
  return `${link.sourceDeviceName ?? 'Unknown'}${port} → ${link.targetDeviceName ?? 'Unknown'}`;
}

/**
 * Reconcile the graph against what the gateways currently hold, then decide what
 * to do about the links none of them could speak for.
 *
 * The two steps are deliberately separate. Re-stating the topology is safe and
 * repeatable; dropping a link nothing vouched for is neither, because a gateway
 * that is merely offline looks exactly like one whose devices genuinely stopped
 * seeing their neighbours. So the sweep reports those and the operator decides.
 */
export function TopologyResyncDialog({ open, onOpenChange, organizationId, regionId }: Props) {
  const resync = useTopologyResync(organizationId, regionId);
  const forget = useForgetConnections(organizationId, regionId);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const result = resync.data;
  const unconfirmed = result?.unconfirmed ?? [];
  const unreached = result?.gateways.filter((g) => !g.reached) ?? [];

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleClose = (next: boolean) => {
    if (!next) {
      resync.reset();
      forget.reset();
      setSelected(new Set());
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Re-audit connections</DialogTitle>
          <DialogDescription>
            Every gateway in view re-states the links it currently holds, and anything it no longer
            reports is dropped from the graph. This reconciles the graph against the gateways; it
            does not re-poll devices, which each gateway already does on its own schedule.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            {resync.isError ? (
              <p className="text-sm text-destructive">
                {resync.error instanceof Error ? resync.error.message : 'The re-audit failed.'}
              </p>
            ) : null}
            <Button onClick={() => { resync.mutate(); }} disabled={resync.isPending}>
              {resync.isPending ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Re-auditing…
                </>
              ) : (
                <>
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                  Start re-audit
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-sev-5" />
              <p>
                {result.devicesSynced} device{result.devicesSynced === 1 ? '' : 's'} re-stated{' '}
                {result.edgesReported} link{result.edgesReported === 1 ? '' : 's'} across{' '}
                {result.gateways.length - unreached.length} gateway
                {result.gateways.length - unreached.length === 1 ? '' : 's'}. Anything they no longer
                report has been removed from the graph.
              </p>
            </div>

            {unreached.length > 0 ? (
              <div className="rounded-md border border-border p-3 text-sm">
                <p className="font-medium mb-1">Gateways that did not answer</p>
                <ul className="space-y-1 text-muted-foreground">
                  {unreached.map((g) => (
                    <li key={g.gatewayId}>
                      {g.gatewayName ?? g.gatewayId}
                      {g.error ? ` — ${g.error}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {unconfirmed.length > 0 ? (
              <div className="space-y-3">
                <div className="flex items-start gap-2 text-sm">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-sev-3" />
                  <p>
                    {unconfirmed.length} link{unconfirmed.length === 1 ? '' : 's'} could not be
                    re-observed, because nothing is currently able to speak for the device that
                    reported {unconfirmed.length === 1 ? 'it' : 'them'}. They are still on the graph.
                    An unreachable gateway is not the same as a link that went away, so removing
                    these is your call.
                  </p>
                </div>

                <ScrollArea className="max-h-64 rounded-md border border-border">
                  <ul className="divide-y divide-border">
                    {unconfirmed.map((link) => (
                      <li key={link.connectionId} className="flex items-start gap-3 p-3 text-sm">
                        <Checkbox
                          id={link.connectionId}
                          checked={selected.has(link.connectionId)}
                          onCheckedChange={() => { toggle(link.connectionId); }}
                          className="mt-0.5"
                        />
                        <label htmlFor={link.connectionId} className="min-w-0 flex-1 cursor-pointer">
                          <span className="block truncate font-medium">{linkLabel(link)}</span>
                          <span className="block text-xs text-muted-foreground">
                            {link.reason} · last reported{' '}
                            {new Date(link.lastSeenAt).toLocaleDateString()}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </ScrollArea>

                {forget.data ? (
                  <p className="text-sm text-muted-foreground">
                    Removed {forget.data.forgotten} link
                    {forget.data.forgotten === 1 ? '' : 's'}.
                    {forget.data.skipped.length > 0
                      ? ` ${String(forget.data.skipped.length)} left alone — a gateway can vouch for ${forget.data.skipped.length === 1 ? 'it' : 'them'} again.`
                      : ''}
                  </p>
                ) : null}
                {forget.isError ? (
                  <p className="text-sm text-destructive">
                    {forget.error instanceof Error ? forget.error.message : 'Removing the links failed.'}
                  </p>
                ) : null}

                <div className="flex items-center gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={selected.size === 0 || forget.isPending}
                    onClick={() => {
                      forget.mutate([...selected], {
                        onSuccess: () => { setSelected(new Set()); },
                      });
                    }}
                  >
                    {forget.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : null}
                    Remove selected ({selected.size})
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelected(new Set(unconfirmed.map((l) => l.connectionId)));
                    }}
                  >
                    Select all
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Every link on the graph was confirmed by the gateway that owns its device.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
